import { useContext, createContext, useRef } from "react";

import PropTypes from "prop-types";

const propTypes = {
  children: PropTypes.node,
};

const FocusManagerContext = createContext();

export const FocusManagerContextProvider = ({ children }) => {
  const focusManagerRefs = useRef({});

  /**
   * @param {HTMLElement} ref Ref of a focusable element
   * @param {string} key Unique key of an element or group of elements.
   *  This must be registered in types/focusElementRefKeys.
   * @param {string} [id] Unique key of an element within a group of elements (optional)
   */
  const setFocusManagerRef = (ref, key, id) => {
    if (!ref) return;
    if (!id) {
      focusManagerRefs.current[key] = ref;
      return;
    }

    if (focusManagerRefs.current[key]) {
      focusManagerRefs.current[key][id] = ref;
      return;
    }

    focusManagerRefs.current[key] = { [id]: ref };
  };

  /**
   * @param {string} key Unique key of an element or group of elements.
   * @param {string} [id] Unique key of an element within a group of elements (optional)
   */
  const focusElement = (key, id) => {
    // requestAnimationFrame is necessary in case the element is not on screen when focusElement is called.
    requestAnimationFrame(() => {
      const baseElement = focusManagerRefs.current[key];
      const element = id ? baseElement?.id : baseElement;

      if (element) {
        element?.focus();
      }

      if (!element) {
        console.error(
          "focusElement was called on an element that does not exist in focusManagerRefs",
        );
      }
    });
  };

  return (
    <FocusManagerContext.Provider
      value={{
        focusElement,
        focusManagerRefs,
        setFocusManagerRef,
      }}
    >
      {children}
    </FocusManagerContext.Provider>
  );
};

FocusManagerContextProvider.propTypes = propTypes;

const useFocusManager = () => {
  const focusManagerContext = useContext(FocusManagerContext);

  if (focusManagerContext === undefined) {
    throw new Error(
      "focusManagerContext must be used within a FocusManagerContextProvider.",
    );
  }

  return focusManagerContext;
};

export default useFocusManager;
